import {useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {formatEther} from "viem";
import {useAccount} from "wagmi";
import {ChainGuard} from "@/components/ChainGuard";
import {TxStatus} from "@/components/TxStatus";
import {WalletPanel} from "@/components/WalletPanel";
import {useRitualWrite} from "@/hooks/useRitualWrite";
import {useQueryFee} from "@/hooks/useSmeltReads";
import {SMELT_ABI} from "@/lib/abi";
import {SMELT_CONTRACT} from "@/lib/config";
import {decodeFilingContent} from "@/lib/decode";

/**
 * Interactive console: pay the query fee and read a structured filing, then claim
 * any accrued credits. Uses sendTransaction + encodeFunctionData (not
 * writeContractAsync) because serveQuery is a payable write to a contract.
 */
export function Console() {
  const {isConnected} = useAccount();
  const [ticker, setTicker] = useState("AAPL");
  const [period, setPeriod] = useState("2023");
  const [result, setResult] = useState<string | null>(null);

  const feeQ = useQueryFee();
  const fee = (feeQ.data as bigint | undefined) ?? 0n;

  const {write, tx} = useRitualWrite();

  // pre-fill from a Filings-card link
  const loc = useLocation();
  useMemo(() => {
    const st = loc.state as {ticker?: string; period?: string} | null;
    if (st?.ticker) setTicker(st.ticker);
    if (st?.period) setPeriod(st.period);
  }, [loc.state]);

  async function onQuery() {
    setResult(null);
    const {receipt} = await write({
      address: SMELT_CONTRACT,
      abi: SMELT_ABI,
      functionName: "serveQuery",
      args: [ticker, period],
      value: fee,
      gas: 400_000n,
      label: `Query ${ticker} ${period}`,
    });
    // serveQuery returns the Filing struct; decode from the call output if available,
    // else fall back to a fresh read.
    const content = decodeFilingFromReceipt(receipt);
    setResult(content ?? "(query accepted — see Filings for the parsed result)");
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-forge-ash">Console</h1>
        <p className="text-sm text-forge-slag mt-1">
          Pay the query fee and read a structured filing on-chain.
        </p>
      </div>

      {!isConnected ? (
        <div className="panel p-6 text-sm text-forge-slag">Connect a wallet to query.</div>
      ) : (
        <ChainGuard>
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="space-y-4">
              <div className="panel p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Ticker">
                    <input
                      value={ticker}
                      onChange={(e) => setTicker(e.target.value.toUpperCase())}
                      className="input"
                      placeholder="AAPL"
                    />
                  </Field>
                  <Field label="Period">
                    <input
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      className="input"
                      placeholder="2023"
                    />
                  </Field>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="label">Query fee</span>
                  <span className="mono text-molten-300">
                    {formatEther(fee)} RITUAL
                  </span>
                </div>

                <button
                  className="btn-primary w-full"
                  onClick={() => onQuery().catch(() => {})}
                  disabled={tx?.state === "CONFIRMING" || tx?.state === "SUBMITTED"}
                >
                  Pay & query
                </button>
              </div>

              <TxStatus tx={tx} />

              {result && (
                <div className="panel-elevated p-4">
                  <p className="label mb-2">Result</p>
                  <pre className="mono text-xs text-refined-cyan whitespace-pre-wrap break-all">
                    {result}
                  </pre>
                </div>
              )}
            </div>

            <WalletPanel />
          </div>
        </ChainGuard>
      )}
    </div>
  );
}

function Field({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/** Try to decode the returned Filing from the tx receipt's spcCalls; fall back null. */
function decodeFilingFromReceipt(receipt: any): string | null {
  try {
    const spcCalls = receipt?.spcCalls;
    if (!spcCalls || spcCalls.length === 0) return null;
    return decodeFilingContent(spcCalls[spcCalls.length - 1].output);
  } catch {
    return null;
  }
}
